import React, { useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import DefaultApiLimit from '../../config/defaultLimit';
import { FirmwareJob } from '../../interface/firmware-jobs.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  fetchFirmwareJobs,
  refreshFirmwareJobs,
  setSortData,
} from '../../redux/slices/FirmwareJobsSlice';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import CustomTable from '../../shared-components/custom-table/CustomTable';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoDateTime from '../../shared-functions/ConvertUTCtoDateTime';
// import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import ConvertUTCtoHHMMSS from '../../shared-functions/ConvertUTCtoHHMMSS';
// import FormatUtcString from '../../shared-functions/FormatUtcString';
import FormatUtcStringWithSeconds from '../../shared-functions/FormatUtcStringWithSeconds';

export interface FirmwareProperties {
  chargingPointSn?: string;
}

/**
 * Firmware management and log view component.
 * @param {FirmwareProperties} properties - The component properties.
 * @returns {React.ReactElement} The rendered Firmware component.
 */
function Firmware(properties: FirmwareProperties = {}): React.ReactElement {
  const { chargingPointSn } = properties;
  const { t, i18n } = useTranslation('devices');
  const dispatch = useAppDispatch();

  const {
    jobs,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.firmwareJobs);

  const column = useMemo(
    () => [
      {
        label: t('date', 'Date'),
        accessorKey: 'createdAt' as keyof FirmwareJob,
        sortAllow: true,
        /**
         * Converting timestamp to UTC date
         * @param {any} job - The job object
         * @returns {React.ReactElement} The rendered element
         */
        customData: (job: any) => <p>{ConvertUTCtoDate(job.createdAt)}</p>,
      },
      {
        label: t('time', 'Time'),
        accessorKey: 'time' as keyof FirmwareJob,
        sortAllow: true,
        /**
         * Converting timestamp to UTC time with seconds
         * @param {any} job - The job object
         * @returns {React.ReactElement} The rendered element
         */
        customData: (job: any) => (
          <p>{FormatUtcStringWithSeconds(ConvertUTCtoHHMMSS(job.createdAt))}</p>
        ),
      },
      {
        label: t('chargingPointSn', 'Charging Point SN'),
        accessorKey: 'chargingPointSn' as keyof FirmwareJob,
        sortAllow: true,
      },
      {
        label: t('fileName', 'File Name'),
        accessorKey: 'fileName' as keyof FirmwareJob,
        sortAllow: true,
      },
      {
        label: t('status', 'Status'),
        accessorKey: 'status' as keyof FirmwareJob,
        sortAllow: true,
      },
      {
        label: t('installedAt', 'Installed At'),
        accessorKey: 'installedAt' as keyof FirmwareJob,
        sortAllow: false,
        /**
         * Render the timestamp of the job's "Installed" status-history entry, if it reached it
         * @param {any} job - The job object
         * @returns {React.ReactElement} The rendered element
         */
        customData: (job: any) => {
          const installedEntry = Array.isArray(job.statusHistory)
            ? job.statusHistory.find((entry: any) => entry.status === 'Installed')
            : null;
          return <p>{installedEntry?.at ? ConvertUTCtoDateTime(installedEntry.at) : '-'}</p>;
        },
      },
      {
        label: t('reportedVersion', 'Reported Version'),
        accessorKey: 'reportedVersionAfterBoot' as keyof FirmwareJob,
        sortAllow: false,
        /**
         * Render reported version
         * @param {any} job - The job object
         * @returns {React.ReactElement | null} The rendered element
         */
        customData: (job: any) => <p>{job.reportedVersionAfterBoot || '-'}</p>,
      },
      {
        label: t('versionMatch', 'Version Match'),
        accessorKey: 'versionMatch' as keyof FirmwareJob,
        sortAllow: false,
        /**
         * Render version match
         * @param {any} job - The job object
         * @returns {React.ReactElement | null} The rendered element
         */
        customData: (job: any) => <p>{job.versionMatch ? t('yes', 'Yes') : t('no', 'No')}</p>,
      },
    ],
    [t],
  );

  /**
   * Handle the fetch data
   * @param {object} parameters - The query parameters
   */
  const fetchData = useCallback(
    (parameters: { page?: number; limit?: number; sortBy?: 'asc' | 'desc' | null; sortField?: string | null }) => {
      if (!chargingPointSn) {
        dispatch(
          setNotificationMessage({
            message: t(
              'chargingPointSnRequiredMessage',
              'A charging point serial number is required to load firmware jobs.',
            ),
            status: true,
            type: 'error',
            code: 400,
          }),
        );
        return;
      }
      const query: any = { ...parameters, chargingPointSn };
      dispatch(fetchFirmwareJobs(query));
    },
    [dispatch, chargingPointSn, t],
  );

  /**
   * Table pagination function
   * @param {any} value The page props
   */
  const pageNateLimitChange = (value: any) => {
    fetchData({
      ...value,
      sortBy,
      sortField,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    fetchData({
      page,
      limit,
      sortBy,
      sortField,
    });
  };

  /**
   * Function for Auto Refresh the data
   */
  const handleAutoRefresh = useCallback(() => {
    if (!chargingPointSn) return;
    const query: any = { page, limit, sortBy, sortField, chargingPointSn };
    dispatch(refreshFirmwareJobs(query));
  }, [dispatch, chargingPointSn, page, limit, sortBy, sortField]);

  /**
   * Function for sorting table data
   * @param {object} sortData The sort configuration
   * @param {any} sortData.sortBy - The sortBy prop
   * @param {any} sortData.sortField - The sortField prop
   */
  const handleSortFunction = (sortData: { sortBy: 'asc' | 'desc'; sortField: string }) => {
    dispatch(setSortData(sortData));
    fetchData({
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  useEffect(() => {
    fetchData({
      page: DefaultApiLimit.page,
      limit,
      sortBy,
      sortField,
    });
  }, [fetchData, limit, sortBy, sortField]);

  return (
    <div className="firmware-jobs-table-wrap" dir={i18n.dir()}>
      <CustomTable
        version="v2"
        header={column}
        data={jobs}
        serialNo
        sizeChangeButtonRequired
        limit={limit}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        loading={loading}
        refershCurrentPages={handleRefresh}
        requiredRefreshToggle
        refreshInterval={15}
        refreshFunction={handleAutoRefresh}
        pageLimitChange={pageNateLimitChange}
        sortData={{
          sortBy,
          sortField,
        }}
        height={340}
        handleSortChange={handleSortFunction}
        filters={[]}
      />
    </div>
  );
}

Firmware.defaultProps = {
  chargingPointSn: '',
};

export default Firmware;
